import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
	ButtonArchive,
	ButtonCancel,
	ButtonClose,
	ButtonSave,
} from '@adflow/frontend/ui-buttons';

import { styles as modalStyles } from './Modal';
import { ModalWithLayout } from './ModalWithLayout';

const styles = {
	header: css(modalStyles.modalHeader, {
		display: 'flex',
		justifyContent: 'space-between',
		paddingTop: '0.75rem',
		paddingBottom: '0.75rem',
	}),
	footer: css(modalStyles.modalFooter, {
		paddingTop: '1.5rem',
		paddingBottom: '0.75rem',
	}),
};

function getContent(
	args: Pick<
		ModalDialogProps,
		| 'type'
		| 'headerContent'
		| 'bodyContent'
		| 'cancelButtonContent'
		| 'confirmButtonContent'
	>,
	t: TFunction<'common'>
) {
	switch (args.type) {
		case 'save': {
			return {
				header: args.headerContent,
				body: args.bodyContent,
				cancel: args.cancelButtonContent,
				confirm: args.confirmButtonContent,
			};
		}
		case 'archive': {
			return {
				header: args.headerContent ?? t('modal.confirmArchive'),
				body: args.bodyContent,
				cancel: args.cancelButtonContent,
				confirm: args.confirmButtonContent,
			};
		}
		case 'discard-changes': {
			return {
				header: args.headerContent ?? t('modal.discardChanges'),
				body: args.bodyContent ?? t('modal.discardChangesBody'),

				cancel: args.cancelButtonContent ?? t('buttons.stay'),
				confirm: args.confirmButtonContent ?? t('buttons.discard'),
			};
		}
		case 'discard-draft': {
			return {
				header: args.headerContent ?? t('modal.discardDraft'),
				body: args.bodyContent ?? t('modal.discardDraftBody'),

				cancel: args.cancelButtonContent ?? t('buttons.stay'),
				confirm: args.confirmButtonContent ?? t('buttons.discard'),
			};
		}

		case 'delete-filter': {
			return {
				header: args.headerContent ?? t('modal.deleteFilter'),
				body: args.bodyContent ?? t('modal.deleteFilterBody'),
				cancel: args.cancelButtonContent ?? t('buttons.cancel'),
				confirm: args.confirmButtonContent ?? t('buttons.delete'),
			};
		}
		case 'dismiss': {
			return {
				header: args.headerContent,
				body: args.bodyContent,
				cancel: args.cancelButtonContent ?? t('buttons.dismiss'),
				disableConfirm: true,
				disableCancel: false,
			};
		}
		default: {
			return {
				header: args.headerContent,
				body: args.bodyContent,
				cancel: args.cancelButtonContent,
				confirm: args.confirmButtonContent,
			};
		}
	}
}

type ModalDialogType =
	| 'save'
	| 'archive'
	| 'discard-changes'
	| 'delete-filter'
	| 'discard-draft'
	| 'dismiss'
	| 'info';

export interface ModalDialogProps {
	type?: ModalDialogType;
	dialogStyleOverrides?: CSSInterpolation | null;
	headerContent?: ReactNode;
	bodyContent?: ReactNode;
	cancelButtonContent?: ReactNode;
	confirmButtonContent?: ReactNode;
	show: boolean;
	disableConfirm?: boolean;
	disableCancel?: boolean;
	'data-disabled'?: boolean;
	onCancel: () => void;
	onConfirm: () => void;
	testId?: string;
	topOffset?: number;
}
export function ModalDialog({
	show,
	onCancel,
	onConfirm,
	type = 'save',
	...props
}: ModalDialogProps) {
	const { t } = useTranslation('common');
	const isSaveButton = type === 'save';
	const ConfirmButton = isSaveButton ? ButtonSave : ButtonArchive;
	const { header, body, cancel, confirm, disableConfirm, disableCancel } =
		getContent({ ...props, type }, t);

	return (
		<ModalWithLayout
			dialogStyleOverrides={props.dialogStyleOverrides}
			show={show}
			onRequestClose={onCancel}
			testid={props.testId ?? 'modal-dialog'}
			topOffset={props.topOffset}
			header={
				<div css={styles.header}>
					{type === 'info' ? (
						<>
							{header}
							<div css={{ marginLeft: '1rem', cursor: 'pointer' }}>
								<ButtonClose onClick={onCancel} />
							</div>
						</>
					) : (
						header
					)}
				</div>
			}
			body={body}
			footerStyle={[styles.footer, type === 'info' && { display: 'none' }]}
			footer={
				<>
					{disableCancel ? null : (
						<ButtonCancel onClick={onCancel}>{cancel}</ButtonCancel>
					)}

					{disableConfirm ? null : (
						<ConfirmButton
							onClick={onConfirm}
							variant="contained"
							data-disabled={props['data-disabled']}
							data-cy={
								type === 'archive' ? 'archive-confirm' : 'dialog-confirm'
							}
							disabled={props['data-disabled']}
						>
							{confirm}
						</ConfirmButton>
					)}
				</>
			}
		/>
	);
}
